import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { action, computed, observable, runInAction } from 'mobx';
import React from 'react';
import type { GridColDef, SortingObservable } from '../../components/Grid/Grid';
import AbstractStore from '../../store/AbstractStore';
import { EVENTS_PATH } from '../../store/constants';
import type { Event } from '../../types/event-types';
import { EventResponse, EventType } from '../../types/event-types';
import { roundTimestamp } from '../../utils/date-utils';
import EventIntervalsHandler from '../../utils/event-intervals-handler';
import { sortByField } from '../../utils/object-utils';
import { ActionsRenderer, ConflictsExistMessage, rowCellRenderer } from './renderers';

dayjs.extend(utc);
dayjs.extend(timezone);

const TIME_INTERVAL_MINUTES = 30;
const UPPER_LIMIT_YEARS = 10;
const REASON_LENGTH = 100;

const emptyItem = (): Event<number> => ({
  reason: '',
  type: '',
  startDateTime: 0,
  endDateTime: 0,
  createdBy: '',
});

const emptyErrorItem = (): Event<string> => ({
  reason: '',
  type: '',
  startDateTime: '',
  endDateTime: '',
  createdBy: '',
});
const isEmptyItem = (m: Event<any>): boolean => Object.keys(m).every(key => !m[key]);
const equalItems = (it1: Event<any>, it2: Event<any>): boolean =>
  Object.keys(it1).every(key => it1[key] === it2[key]);

export const toLocalDateTimeWithTimezoneName = (timestamp: number | Date): string =>
  `${dayjs(timestamp).format('D-MMM YYYY[, at ]h:mm a')} ${dayjs.tz.guess()}`;

interface Loaders {
  page?: boolean;
  action?: boolean;
}

export default class ServiceEventsStore extends AbstractStore {
  @observable accessor loaders: Loaders = {};

  @observable accessor items: Event<number>[] = [];

  @observable accessor sorting: SortingObservable<Event<number>> = {};

  @observable accessor eventIntervalsHandler: EventIntervalsHandler = new EventIntervalsHandler(
    [],
    TIME_INTERVAL_MINUTES,
  );

  @observable accessor error: string | null = null;

  @observable accessor currentItem: Event<number> = emptyItem();

  @observable accessor currentItemError: Event<string> = emptyErrorItem();

  @observable accessor currentItemIndex: number | undefined = undefined;

  @observable accessor addMode = false;

  @computed
  public get colDefs(): GridColDef<Event<number>>[] {
    return [
      {
        headerName: 'Reason',
        field: 'reason',
        renderer: rowCellRenderer('reason'),
        sortable: true,
      },
      {
        headerName: 'Start',
        field: 'startDateTime',
        renderer: rowCellRenderer('startDateTime'),
        sortable: true,
      },
      {
        headerName: 'End',
        field: 'endDateTime',
        renderer: rowCellRenderer('endDateTime'),
        sortable: true,
      },
      { headerName: 'Scheduled By', field: 'createdBy', sortable: true },
      { headerName: 'Actions', field: 'actions', renderer: ActionsRenderer },
    ];
  }

  @computed
  public get sortedItems(): Event<number>[] {
    let sorted = this.items.filter(i => !equalItems(i, emptyErrorItem()));
    if (this.sorting.field) {
      sorted = sortByField(sorted, this.sorting.field);
      if (this.sorting.order === 'DESC') {
        sorted = sorted.reverse();
      }
    }
    if (this.addMode) sorted.push(emptyItem());
    return sorted;
  }

  @action
  public async init(): Promise<void> {
    runInAction(() => {
      this.loaders.page = true;
      this.addMode = false;
      this.sorting = { field: 'startDateTime', order: 'DESC' };
      this.eventIntervalsHandler.setAllowedInterval({
        start: dayjs().valueOf(),
        end: dayjs().add(UPPER_LIMIT_YEARS, 'years').valueOf(),
      });
    });
    await this.loadAllServiceEvents();
    runInAction(() => (this.loaders.page = false));
  }

  @action
  public async refresh(): Promise<void> {
    if (this.loaders.action) return;
    runInAction(() => (this.loaders.action = true));
    await this.loadAllServiceEvents();
    runInAction(() => (this.loaders.action = false));
  }

  @action
  public addItem(): void {
    if (this.loaders.action) return;
    this.addMode = true;
    this.currentItem = emptyItem();
    this.currentItem.type = EventType.SCHEDULED_MAINTENANCE;
    this.currentItem.startDateTime = roundTimestamp(dayjs().valueOf(), TIME_INTERVAL_MINUTES, true);
    this.currentItem.endDateTime = dayjs(this.currentItem.startDateTime)
      .add(TIME_INTERVAL_MINUTES, 'minutes')
      .valueOf();
    this.currentItemError = emptyErrorItem();
    this.currentItemIndex = this.sortedItems.length - 1;
  }

  @action
  public editItem(index: number): void {
    if (this.loaders.action) return;
    this.addMode = false;
    this.currentItem = { ...this.sortedItems[index] };
    this.currentItemError = emptyErrorItem();
    this.currentItemIndex = index;
  }

  @action
  public deleteItem(index: number): void {
    if (this.loaders.action) return;
    this.root.globalModalStore.showOkCancelPopup(
      'Confirm event deletion',
      'Are you sure you want to delete this event?',
      'OK',
      'Cancel',
      () => this.doDeleteItem(index),
    );
  }

  @action
  public cancelEditingItem(): void {
    if (this.currentItemIndex === undefined) return;
    this.addMode = false;
    this.currentItemIndex = undefined;
  }

  @action
  public async validateAndUpdateItem(): Promise<void> {
    if (this.currentItemIndex === undefined) return;
    // to prevent multiple calls of this method
    const currentIndex = this.currentItemIndex;
    runInAction(() => {
      this.currentItemIndex = undefined;
      const { reason, startDateTime, endDateTime } = this.currentItem;
      this.currentItemError = emptyErrorItem();
      if (!reason) {
        this.currentItemError.reason = 'Reason should be set';
      } else if (reason.length > REASON_LENGTH) {
        this.currentItemError.reason = '100 character max';
      }
      if (startDateTime >= endDateTime) {
        this.currentItemError.startDateTime = 'Start should be before End';
        this.currentItemError.endDateTime = 'End should be after Start';
      }
    });
    if (!isEmptyItem(this.currentItemError)) {
      runInAction(() => (this.currentItemIndex = currentIndex));
    } else {
      if (!equalItems(this.currentItem, this.sortedItems[currentIndex])) {
        await this.updateItem(currentIndex);
      }
      runInAction(() => (this.currentItemIndex = undefined));
    }
  }

  @action
  private async loadAllServiceEvents(): Promise<void> {
    const serviceEvents = await this.root.apiStore.findAllServiceEvents();
    runInAction(() => {
      this.items = serviceEvents;
      this.currentItemIndex = undefined;
    });
  }

  private async updateItem(currentIndex: number): Promise<void> {
    const { addMode } = this;
    runInAction(() => {
      this.loaders.action = true;
      this.addMode = false;
    });
    try {
      const item = await this.root.apiStore.backendPost<EventResponse>(EVENTS_PATH, {
        action: addMode ? 'CREATE' : 'UPDATE',
        ...this.currentItem,
      });
      runInAction(() => {
        if (addMode) {
          this.items.push(item);
        } else {
          const index = this.items.findIndex(i => i.id === this.sortedItems[currentIndex].id);
          if (index >= 0) this.items[index] = item;
        }
      });
      if (item.isConflict) {
        this.root.globalModalStore.showOkPopup(
          'Event Saved - Conflicts Exist',
          React.createElement(ConflictsExistMessage(this.root.apiStore.userEmail)),
        );
      } else {
        this.root.globalModalStore.showOkPopup(
          'Event Saved',
          'There were no upgrades scheduled between the chosen start and end times.',
        );
      }
    } finally {
      runInAction(() => (this.loaders.action = false));
    }
  }

  private async doDeleteItem(currentIndex: number): Promise<void> {
    if (this.loaders.action) return;
    runInAction(() => (this.loaders.action = true));
    try {
      const { id } = this.sortedItems[currentIndex];
      await this.root.apiStore.backendPost(EVENTS_PATH, { action: 'DELETE', id });
      runInAction(() => (this.items = this.items.filter(e => e.id !== id)));
    } catch {
      // ignore
    }
    runInAction(() => {
      this.currentItemIndex = undefined;
      this.loaders.action = false;
    });
  }
}
